// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import type { CitizenMessageThread } from 'lib-common/generated/api-types/messaging'
import type {
  MessageAccountId,
  MessageThreadId
} from 'lib-common/generated/api-types/shared'
import { NotificationsContext } from 'lib-components/Notifications'
import { OnEnterView } from 'lib-components/OnEnterView'
import { LegacyButton } from 'lib-components/atoms/buttons/LegacyButton'
import ReturnButton from 'lib-components/atoms/buttons/ReturnButton'
import { buttonBounceAnimation } from 'lib-components/atoms/buttons/button-commons'
import { SpinnerSegment } from 'lib-components/atoms/state/Spinner'
import { desktopMin, tabletMin } from 'lib-components/breakpoints'
import { FixedSpaceRow } from 'lib-components/layout/flex-helpers'
import { TabletAndDesktop } from 'lib-components/layout/responsive-layout'
import { H1 } from 'lib-components/typography'
import { defaultMargins, Gap } from 'lib-components/white-space'
import colors from 'lib-customizations/common'
import { faPlus } from 'lib-icons'

import { renderResult } from '../async-rendering'
import { useTranslation } from '../localization'
import { mobileBottomNavHeight } from '../navigation/const'

import { ConfirmDeleteThread } from './ConfirmDeleteThread'
import ThreadListItem from './ThreadListItem'
import { MessageContext } from './state'
import { isRedactedThread } from './utils'

const hasUnreadMessages = (
  thread: CitizenMessageThread,
  accountId: MessageAccountId
) =>
  isRedactedThread(thread)
    ? thread.hasUnreadMessages
    : thread.messages.some((m) => !m.readAt && m.sender.id !== accountId)

interface Props {
  accountId: MessageAccountId
  selectThread: (threadId: MessageThreadId) => void
  closeThread: () => void
  setEditorVisible: (value: boolean) => void
  newMessageButtonEnabled: boolean
}

export default React.memo(function ThreadList({
  accountId,
  selectThread,
  closeThread,
  setEditorVisible,
  newMessageButtonEnabled
}: Props) {
  const t = useTranslation()
  const { selectedThread, threads, loadMoreThreads, hasMoreThreads } =
    useContext(MessageContext)
  const { addTimedNotification } = useContext(NotificationsContext)
  const [confirmDelete, setConfirmDelete] = useState<MessageThreadId>()

  const mainHeading = useRef<HTMLHeadingElement | null>(null)

  useEffect(() => {
    // focus on the accessibility heading
    if (mainHeading.current) {
      mainHeading.current.focus()
    }
  }, [])

  return (
    <>
      {selectedThread && (
        <MobileOnly>
          <ReturnButton
            label={t.common.return}
            onClick={closeThread}
            margin={defaultMargins.s}
          />
        </MobileOnly>
      )}
      <Container className={selectedThread ? 'desktop-only' : undefined}>
        <Gap $size="s" $sizeOnMobile="m" />
        <HeaderContainer>
          <H1 $noMargin ref={mainHeading} tabIndex={-1}>
            {t.messages.inboxTitle}
          </H1>
        </HeaderContainer>
        <Gap $size="xs" $sizeOnMobile="m" />
        <TabletAndDesktop>
          <DottedLine />
          <Gap $size="s" />
          <HeaderContainer>
            <LegacyButton
              text={t.messages.messageEditor.newMessage}
              onClick={() => setEditorVisible(true)}
              primary
              data-qa="new-message-btn-desktop"
              id="new-message-btn"
              disabled={!newMessageButtonEnabled}
            />
          </HeaderContainer>
          <Gap $size="s" />
        </TabletAndDesktop>
        <MobileOnly>
          <FloatingButton
            onClick={() => setEditorVisible(true)}
            primary
            data-qa="new-message-btn-mobile"
            disabled={!newMessageButtonEnabled}
          >
            <FixedSpaceRow $spacing="xs" $alignItems="center">
              <FontAwesomeIcon icon={faPlus} />
              <div>{t.messages.messageEditor.newMessage}</div>
            </FixedSpaceRow>
          </FloatingButton>
        </MobileOnly>

        {renderResult(threads, (threads) =>
          threads.length > 0 ? (
            <>
              <ThreadListItems>
                {threads.map((thread) => (
                  <li key={thread.id}>
                    <ThreadListItem
                      thread={thread}
                      onClick={() => selectThread(thread.id)}
                      onDelete={() => setConfirmDelete(thread.id)}
                      active={selectedThread?.id === thread.id}
                      hasUnreadMessages={hasUnreadMessages(thread, accountId)}
                    />
                  </li>
                ))}
              </ThreadListItems>
              {hasMoreThreads ? (
                <>
                  <OnEnterView onEnter={loadMoreThreads} />
                  <SpinnerSegment />
                </>
              ) : null}
            </>
          ) : (
            <>
              <SolidLine />
              <ThreadListContainer>
                <Gap $size="s" />
                <NoMessagesInfo>{t.messages.noMessagesInfo}</NoMessagesInfo>
                <Gap $size="XXL" />
              </ThreadListContainer>
            </>
          )
        )}
        {confirmDelete !== undefined ? (
          <ConfirmDeleteThread
            threadId={confirmDelete}
            onClose={() => setConfirmDelete(undefined)}
            onSuccess={() => {
              setConfirmDelete(undefined)
              addTimedNotification({
                children: t.messages.confirmDelete.success,
                dataQa: 'thread-deleted-notification'
              })
            }}
          />
        ) : null}
      </Container>
    </>
  )
})

const MobileOnly = styled.div`
  display: none;

  @media (max-width: ${tabletMin}) {
    display: block;
    margin: 0;
  }
`

const DottedLine = styled.hr`
  width: 100%;
  border: 1px dashed ${colors.grayscale.g35};
  border-top-width: 0;
`

const SolidLine = styled.hr`
  width: 100%;
  border: 1px solid ${colors.grayscale.g15};
  border-top-width: 0;
`

const NoMessagesInfo = styled.div`
  color: ${colors.grayscale.g70};
`

const Container = styled.div`
  min-width: 35%;
  max-width: 400px;
  min-height: 500px;
  background-color: ${colors.grayscale.g0};

  @media (max-width: ${desktopMin}) {
    overflow-y: visible;
  }

  @media (min-width: ${desktopMin}) {
    overflow-y: auto;
  }

  @media (max-width: 750px) {
    min-width: 50%;
  }

  @media (max-width: ${tabletMin}) {
    width: 100%;
    max-width: 100%;
    min-height: auto;
  }

  &.desktop-only {
    @media (max-width: ${tabletMin}) {
      display: none;
    }
  }
`

const HeaderContainer = styled.div`
  padding-left: 5%;
`

const ThreadListContainer = styled.div`
  padding-left: 5%;
`

const ThreadListItems = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const FloatingButton = styled(LegacyButton)`
  position: fixed;
  bottom: calc(${defaultMargins.s} + ${mobileBottomNavHeight}px);
  right: ${defaultMargins.s};
  border-radius: 40px;
  z-index: 10;

  ${buttonBounceAnimation};
  animation-delay: 0.5s;
`
